/**
 * Normalize a property address into a human-readable string.
 *
 * Properties are stored with inconsistent shapes (the backend
 * src/lib/address.js documents the same set):
 *   - { address: "12 Acacia Avenue, London, SW1 1AA" }   legacy flat string
 *   - { address: { line1, line2, city, postcode } }       structured object
 *   - { address: "12 Acacia Avenue", postcode: "SW1 1AA" } split top-level
 *   - { address: "12 Acacia Avenue", postcode: { line1, ... } } legacy split
 *     where postcode was itself populated with the whole address object
 *
 * The last shape is what produced the production white screen on /properties:
 * components rendered {property.postcode} directly, and React throws
 * "Objects are not valid as a React child (found: object with keys
 * {line1, line2, city, postcode})" (minified React error #31), which
 * unmounts the whole tree.
 *
 * Every address render must go through this helper.
 */

export interface PropertyAddressLike {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  postcode?: string | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asText = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';

/**
 * Coerce any stored address-ish value into text. Objects are formatted rather
 * than returned, so a caller can never hand React a raw object.
 */
export const addressToText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(addressToText).filter(Boolean).join(', ');
  if (isRecord(value)) {
    const obj = value as PropertyAddressLike;
    const parts = [obj.line1, obj.line2, obj.city, obj.postcode]
      .map(asText)
      .filter(Boolean);
    return parts.join(', ');
  }
  return asText(value);
};

/** Structured parts of an address, with object and string forms both handled. */
export const addressParts = (property: any): {
  line1: string;
  line2: string;
  city: string;
  postcode: string;
} => {
  const source: any = property || {};
  const obj = isRecord(source.address)
    ? (source.address as PropertyAddressLike)
    : isRecord(source.addressObj)
      ? (source.addressObj as PropertyAddressLike)
      : null;

  if (obj) {
    return {
      line1: asText(obj.line1),
      line2: asText(obj.line2),
      city: asText(obj.city),
      postcode: asText(obj.postcode),
    };
  }

  return {
    line1: addressToText(source.address) || asText(source.propertyName),
    line2: '',
    city: asText(source.city),
    postcode: addressToText(source.postcode),
  };
};

/** Single-line address for display, e.g. property cards and breadcrumbs. */
export const formatPropertyAddress = (property: any, fallback = 'Unknown Address'): string => {
  if (!property) return fallback;

  const { line1, line2, city, postcode } = addressParts(property);
  if (line1 || line2 || city || postcode) {
    return [line1, line2, city, postcode].filter(Boolean).join(', ') || fallback;
  }
  if (property.propertyName) return String(property.propertyName);
  return fallback;
};

/** Postcode only, always text. */
export const formatPropertyPostcode = (property: any, fallback = ''): string =>
  addressParts(property).postcode || fallback;

/** City only, always text. */
export const formatPropertyCity = (property: any, fallback = ''): string =>
  addressParts(property).city || fallback;

/** First line only, always text. */
export const formatPropertyLine1 = (property: any, fallback = 'Unknown Address'): string =>
  addressParts(property).line1 || fallback;

/**
 * Coerce ANY stored value into renderable text.
 *
 * Address fields were the first thing to blank /properties, but the same class
 * of crash exists for every other field the API can return as an object:
 * bedrooms, rentAmount, prsRegistrationNumber and type have all been observed
 * as structured values rather than scalars. Rendering one of those raw throws
 * minified React error #31, which unmounts the entire tree and leaves a blank
 * page. Route every non-address field through this so shape drift can never
 * take the page down again.
 */
export const safeText = (value: unknown, fallback = ''): string => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map((entry) => safeText(entry)).filter(Boolean).join(', ') || fallback;
  }
  if (isRecord(value)) {
    const obj = value as Record<string, unknown>;
    // Prefer the human-facing key when a structured value carries one.
    for (const key of ['label', 'name', 'display', 'text', 'value', 'amount', 'code']) {
      const candidate = obj[key];
      if (typeof candidate === 'string' || typeof candidate === 'number') {
        return String(candidate);
      }
    }
    return Object.values(obj)
      .map((entry) => safeText(entry))
      .filter(Boolean)
      .join(', ') || fallback;
  }
  return fallback;
};